import {
  HighStatusDataInfoIcon20,
  LowStatusDataInfoIcon20,
  MediumStatusDataInfoIcon20,
  OkStatusDataInfoIcon20,
  RASStatusDataInfoIcon20,
} from '@assets/icons/20x20/target-ic-dataInfo';
import {
  HighStatusDataInfoIcon24,
  LowStatusDataInfoIcon24,
  MediumStatusDataInfoIcon24,
  OkStatusDataInfoIcon24,
  RASStatusDataInfoIcon24,
} from '@assets/icons/24x24/target-ic-dataInfo';
import {
  AircraftSureIcon,
  AircraftUnsureIcon,
  BirdSureIcon,
  BirdUnsureIcon,
  DetectorSureIcon,
  DetectorUnsureIcon,
  DroneEnemySureIcon,
  DroneEnemyUnsureIcon,
  DroneFriendSureIcon,
  DroneFriendUnsureIcon,
  DroneSuspectSureIcon,
  DroneSuspectUnsureIcon,
  DroneUnknownSureIcon,
  DroneUnknownUnsureIcon,
  DroneWatchSureIcon,
  DroneWatchUnsureIcon,
  GroundVehicleSureIcon,
  GroundVehicleUnsureIcon,
  HelicopterSureIcon,
  HelicopterUnsureIcon,
  PersonSureIcon,
  PersonUnsureIcon,
  RemoteControlEnemySureIcon,
  RemoteControlEnemyUnsureIcon,
  RemoteControlFriendSureIcon,
  RemoteControlFriendUnsureIcon,
  RemoteControlSuspectSureIcon,
  RemoteControlSuspectUnsureIcon,
  RemoteControlUnknownSureIcon,
  RemoteControlUnknownUnsureIcon,
  RemoteControlWatchSureIcon,
  RemoteControlWatchUnsureIcon,
  ShipSureIcon,
  ShipUnsureIcon,
  UnknownSureIcon,
  UnknownUnsureIcon,
  VcaSureIcon,
  VcaUnsureIcon,
} from '@assets/icons/70x70/target';
import {
  AircraftSureNeutralIcon,
  AircraftUnsureNeutralIcon,
  BirdSureNeutralIcon,
  BirdUnsureNeutralIcon,
  DetectorSureNeutralIcon,
  DetectorUnsureNeutralIcon,
  DroneEnemySureNeutralIcon,
  DroneEnemyUnsureNeutralIcon,
  DroneFriendSureNeutralIcon,
  DroneFriendUnsureNeutralIcon,
  DroneSuspectSureNeutralIcon,
  DroneSuspectUnsureNeutralIcon,
  DroneUnknownSureNeutralIcon,
  DroneUnknownUnsureNeutralIcon,
  DroneWatchSureNeutralIcon,
  DroneWatchUnsureNeutralIcon,
  GroundVehicleSureNeutralIcon,
  GroundVehicleUnsureNeutralIcon,
  HelicopterSureNeutralIcon,
  HelicopterUnsureNeutralIcon,
  PersonSureNeutralIcon,
  PersonUnsureNeutralIcon,
  RemoteControlEnemySureNeutralIcon,
  RemoteControlEnemyUnsureNeutralIcon,
  RemoteControlFriendSureNeutralIcon,
  RemoteControlFriendUnsureNeutralIcon,
  RemoteControlSuspectSureNeutralIcon,
  RemoteControlSuspectUnsureNeutralIcon,
  RemoteControlUnknownSureNeutralIcon,
  RemoteControlUnknownUnsureNeutralIcon,
  RemoteControlWatchSureNeutralIcon,
  RemoteControlWatchUnsureNeutralIcon,
  ShipSureNeutralIcon,
  ShipUnsureNeutralIcon,
  UnknownSureNeutralIcon,
  UnknownUnsureNeutralIcon,
  VcaSureNeutralIcon,
  VcaUnsureNeutralIcon,
} from '@assets/icons/engagement';
import { ColorLabelEnum, iconColor } from '@utils/map/map.constants';
import { FunctionComponent, SVGProps } from 'react';

import { TacticalClearanceEnum } from '@/types/c2/c2.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';
import { IdentificationEnum, ReliabilityEnum } from '@/types/sensor/identification.types';

export enum IconTargetNameEnum {
  BIRD = 'bird',
  BOAT = 'boat',
  DETECTOR = 'detector',
  DRONE = 'drone',
  VCA = 'vca',
  HELICO = 'helico',
  HUMAN = 'human',
  PLANE = 'plane',
  REMOTE_CONTROL = 'remoteControl',
  UNKNOWN = 'unknown',
  VEHICULE = 'vehicule',
}

export const targetIconColorFromClassification: Record<ClassificationEnum, string> = {
  ENEMY: iconColor[ColorLabelEnum.RED],
  SUSPECT: iconColor[ColorLabelEnum.ORANGE],
  WATCH: iconColor[ColorLabelEnum.YELLOW],
  FRIEND: iconColor[ColorLabelEnum.GREEN],
  UNKNOWN: iconColor[ColorLabelEnum.GRAY],
  UNCLASSIFIED: iconColor[ColorLabelEnum.WHITE],
} as const;

export const targetIconIdentification: Record<IdentificationEnum, IconTargetNameEnum> = {
  AIRCRAFT: IconTargetNameEnum.PLANE,
  VCA: IconTargetNameEnum.VCA,
  BIRD: IconTargetNameEnum.BIRD,
  BUILDING: IconTargetNameEnum.DETECTOR,
  DRONE: IconTargetNameEnum.DRONE,
  GROUND_VEHICLE: IconTargetNameEnum.VEHICULE,
  HELICOPTER: IconTargetNameEnum.HELICO,
  PERSON: IconTargetNameEnum.HUMAN,
  REMOTE_CONTROL: IconTargetNameEnum.REMOTE_CONTROL,
  SHIP: IconTargetNameEnum.BOAT,
  UNKNOWN: IconTargetNameEnum.UNKNOWN,
} as const;

export enum IconTypeEnum {
  NEUTRAL = 'NEUTRAL',
  NORMAL = 'NORMAL',
}

export const simpleTargetIcons: Record<
  Exclude<IdentificationEnum, IdentificationEnum.DRONE | IdentificationEnum.REMOTE_CONTROL>,
  Record<ReliabilityEnum, Record<IconTypeEnum, FunctionComponent<SVGProps<SVGSVGElement>>>>
> = {
  [IdentificationEnum.AIRCRAFT]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: AircraftSureIcon,
      [IconTypeEnum.NEUTRAL]: AircraftSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: AircraftUnsureIcon,
      [IconTypeEnum.NEUTRAL]: AircraftUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.VCA]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: VcaSureIcon,
      [IconTypeEnum.NEUTRAL]: VcaSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: VcaUnsureIcon,
      [IconTypeEnum.NEUTRAL]: VcaUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.BIRD]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: BirdSureIcon,
      [IconTypeEnum.NEUTRAL]: BirdSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: BirdUnsureIcon,
      [IconTypeEnum.NEUTRAL]: BirdUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.BUILDING]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: DetectorSureIcon,
      [IconTypeEnum.NEUTRAL]: DetectorSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: DetectorUnsureIcon,
      [IconTypeEnum.NEUTRAL]: DetectorUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.GROUND_VEHICLE]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: GroundVehicleSureIcon,
      [IconTypeEnum.NEUTRAL]: GroundVehicleSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: GroundVehicleUnsureIcon,
      [IconTypeEnum.NEUTRAL]: GroundVehicleUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.HELICOPTER]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: HelicopterSureIcon,
      [IconTypeEnum.NEUTRAL]: HelicopterSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: HelicopterUnsureIcon,
      [IconTypeEnum.NEUTRAL]: HelicopterUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.PERSON]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: PersonSureIcon,
      [IconTypeEnum.NEUTRAL]: PersonSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: PersonUnsureIcon,
      [IconTypeEnum.NEUTRAL]: PersonUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.SHIP]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: ShipSureIcon,
      [IconTypeEnum.NEUTRAL]: ShipSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: ShipUnsureIcon,
      [IconTypeEnum.NEUTRAL]: ShipUnsureNeutralIcon,
    },
  },
  [IdentificationEnum.UNKNOWN]: {
    [ReliabilityEnum.SURE]: {
      [IconTypeEnum.NORMAL]: UnknownSureIcon,
      [IconTypeEnum.NEUTRAL]: UnknownSureNeutralIcon,
    },
    [ReliabilityEnum.UNSURE]: {
      [IconTypeEnum.NORMAL]: UnknownUnsureIcon,
      [IconTypeEnum.NEUTRAL]: UnknownUnsureNeutralIcon,
    },
  },
};

export const complexTargetIcons: Record<
  IdentificationEnum.DRONE | IdentificationEnum.REMOTE_CONTROL,
  Record<ClassificationEnum, Record<ReliabilityEnum, Record<IconTypeEnum, FunctionComponent<SVGProps<SVGSVGElement>>>>>
> = {
  [IdentificationEnum.DRONE]: {
    [ClassificationEnum.ENEMY]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: DroneEnemySureIcon,
        [IconTypeEnum.NEUTRAL]: DroneEnemySureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: DroneEnemyUnsureIcon,
        [IconTypeEnum.NEUTRAL]: DroneEnemyUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.FRIEND]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: DroneFriendSureIcon,
        [IconTypeEnum.NEUTRAL]: DroneFriendSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: DroneFriendUnsureIcon,
        [IconTypeEnum.NEUTRAL]: DroneFriendUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.SUSPECT]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: DroneSuspectSureIcon,
        [IconTypeEnum.NEUTRAL]: DroneSuspectSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: DroneSuspectUnsureIcon,
        [IconTypeEnum.NEUTRAL]: DroneSuspectUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.UNCLASSIFIED]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: DroneUnknownSureIcon,
        [IconTypeEnum.NEUTRAL]: DroneUnknownSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: DroneUnknownUnsureIcon,
        [IconTypeEnum.NEUTRAL]: DroneUnknownUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.UNKNOWN]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: DroneUnknownSureIcon,
        [IconTypeEnum.NEUTRAL]: DroneUnknownSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: DroneUnknownUnsureIcon,
        [IconTypeEnum.NEUTRAL]: DroneUnknownUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.WATCH]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: DroneWatchSureIcon,
        [IconTypeEnum.NEUTRAL]: DroneWatchSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: DroneWatchUnsureIcon,
        [IconTypeEnum.NEUTRAL]: DroneWatchUnsureNeutralIcon,
      },
    },
  },
  [IdentificationEnum.REMOTE_CONTROL]: {
    [ClassificationEnum.ENEMY]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlEnemySureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlEnemySureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlEnemyUnsureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlEnemyUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.FRIEND]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlFriendSureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlFriendSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlFriendUnsureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlFriendUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.SUSPECT]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlSuspectSureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlSuspectSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlSuspectUnsureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlSuspectUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.UNCLASSIFIED]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlUnknownSureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlUnknownSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlUnknownUnsureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlUnknownUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.UNKNOWN]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlUnknownSureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlUnknownSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlUnknownUnsureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlUnknownUnsureNeutralIcon,
      },
    },
    [ClassificationEnum.WATCH]: {
      [ReliabilityEnum.SURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlWatchSureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlWatchSureNeutralIcon,
      },
      [ReliabilityEnum.UNSURE]: {
        [IconTypeEnum.NORMAL]: RemoteControlWatchUnsureIcon,
        [IconTypeEnum.NEUTRAL]: RemoteControlWatchUnsureNeutralIcon,
      },
    },
  },
};

export enum DataInfoStatusEnum {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  OK = 'OK',
  RAS = 'RAS',
}

export enum DataInfoSizeEnum {
  NOTIFICATION_CARD = 'NOTIFICATION_CARD',
  TARGET_DETAIL = 'TARGET_DETAIL',
}

export const dataStatusInfoIcons: Record<
  DataInfoStatusEnum,
  Record<DataInfoSizeEnum, FunctionComponent<SVGProps<SVGSVGElement>>>
> = {
  [DataInfoStatusEnum.HIGH]: {
    [DataInfoSizeEnum.NOTIFICATION_CARD]: HighStatusDataInfoIcon20,
    [DataInfoSizeEnum.TARGET_DETAIL]: HighStatusDataInfoIcon24,
  },
  [DataInfoStatusEnum.MEDIUM]: {
    [DataInfoSizeEnum.NOTIFICATION_CARD]: MediumStatusDataInfoIcon20,
    [DataInfoSizeEnum.TARGET_DETAIL]: MediumStatusDataInfoIcon24,
  },
  [DataInfoStatusEnum.LOW]: {
    [DataInfoSizeEnum.NOTIFICATION_CARD]: LowStatusDataInfoIcon20,
    [DataInfoSizeEnum.TARGET_DETAIL]: LowStatusDataInfoIcon24,
  },
  [DataInfoStatusEnum.OK]: {
    [DataInfoSizeEnum.NOTIFICATION_CARD]: OkStatusDataInfoIcon20,
    [DataInfoSizeEnum.TARGET_DETAIL]: OkStatusDataInfoIcon24,
  },
  [DataInfoStatusEnum.RAS]: {
    [DataInfoSizeEnum.NOTIFICATION_CARD]: RASStatusDataInfoIcon20,
    [DataInfoSizeEnum.TARGET_DETAIL]: RASStatusDataInfoIcon24,
  },
};

export const tacticalClearanceDataStatus: Record<TacticalClearanceEnum, DataInfoStatusEnum> = {
  [TacticalClearanceEnum.NOTHING_SIGNIFICANT]: DataInfoStatusEnum.RAS,
  [TacticalClearanceEnum.VERIFIED]: DataInfoStatusEnum.OK,
  [TacticalClearanceEnum.UNDER_OBSERVATION]: DataInfoStatusEnum.LOW,
  [TacticalClearanceEnum.UNDER_SUPERVISION]: DataInfoStatusEnum.MEDIUM,
  [TacticalClearanceEnum.UNDER_INTERVENTION]: DataInfoStatusEnum.HIGH,
};
